import { EmploymentType } from './employmentType'

export enum DrugPanelType {
  FIVE_PANEL = 'FIVE_PANEL',
  TEN_PANEL = 'TEN_PANEL',
  THIRTEEN_PANEL = 'THIRTEEN_PANEL',
  FIVE_PANEL_WITHOUT_THC = 'FIVE_PANEL_WITHOUT_THC',
  TEN_PANEL_WITHOUT_THC = 'TEN_PANEL_WITHOUT_THC',
  THIRTEEN_PANEL_WITHOUT_THC = 'THIRTEEN_PANEL_WITHOUT_THC',
  FIVE_PANEL_POST_ACCIDENT = 'FIVE_PANEL_POST_ACCIDENT',
  TEN_PANEL_POST_ACCIDENT = 'TEN_PANEL_POST_ACCIDENT',
  THIRTEEN_PANEL_POST_ACCIDENT = 'THIRTEEN_PANEL_POST_ACCIDENT',
  FIVE_PANEL_WITHOUT_THC_POST_ACCIDENT = 'FIVE_PANEL_WITHOUT_THC_POST_ACCIDENT',
  TEN_PANEL_WITHOUT_THC_POST_ACCIDENT = 'TEN_PANEL_WITHOUT_THC_POST_ACCIDENT',
  THIRTEEN_PANEL_WITHOUT_THC_POST_ACCIDENT = 'THIRTEEN_PANEL_WITHOUT_THC_POST_ACCIDENT',
  FIVE_PANEL_RANDOM = 'FIVE_PANEL_RANDOM',
  TEN_PANEL_RANDOM = 'TEN_PANEL_RANDOM',
  THIRTEEN_PANEL_RANDOM = 'THIRTEEN_PANEL_RANDOM',
  FIVE_PANEL_WITHOUT_THC_RANDOM = 'FIVE_PANEL_WITHOUT_THC_RANDOM',
  TEN_PANEL_WITHOUT_THC_RANDOM = 'TEN_PANEL_WITHOUT_THC_RANDOM',
  THIRTEEN_PANEL_WITHOUT_THC_RANDOM = 'THIRTEEN_PANEL_WITHOUT_THC_RANDOM',
  SIX_PANEL_RAPID = 'SIX_PANEL_RAPID',
  TEN_PANEL_RAPID = 'TEN_PANEL_RAPID',
  LABCORP_EXPRESS_6_PANEL = 'LABCORP_EXPRESS_6_PANEL',
  LABCORP_EXPRESS_10_PANEL = 'LABCORP_EXPRESS_10_PANEL',
  LABCORP_5_PANEL = 'LABCORP_5_PANEL',
  LABCORP_10_PANEL = 'LABCORP_10_PANEL',
}

export enum DrugScreeningVendor {
  LABCORP = 'LABCORP',
  QUEST = 'CNET_TECHNOLOGIES',
  ONSITE = 'ONSITE',
  OTHER = 'OTHER',
  NO_PREFERENCE = 'NO_PREFERENCE',
}

export const VENDOR_PANEL_MAP: Record<DrugScreeningVendor, DrugPanelType[]> = {
  [DrugScreeningVendor.LABCORP]: [
    DrugPanelType.LABCORP_EXPRESS_6_PANEL,
    DrugPanelType.LABCORP_EXPRESS_10_PANEL,
    DrugPanelType.LABCORP_5_PANEL,
    DrugPanelType.LABCORP_10_PANEL,
  ],
  [DrugScreeningVendor.QUEST]: [
    DrugPanelType.FIVE_PANEL,
    DrugPanelType.TEN_PANEL,
    DrugPanelType.THIRTEEN_PANEL,
    DrugPanelType.FIVE_PANEL_WITHOUT_THC,
    DrugPanelType.TEN_PANEL_WITHOUT_THC,
    DrugPanelType.THIRTEEN_PANEL_WITHOUT_THC,
    DrugPanelType.SIX_PANEL_RAPID,
    DrugPanelType.TEN_PANEL_RAPID,
  ],
  [DrugScreeningVendor.ONSITE]: [],
  [DrugScreeningVendor.OTHER]: [],
  [DrugScreeningVendor.NO_PREFERENCE]: [],
}

export enum DrugTestType {
  PRE_EMPLOYMENT = 'PRE_EMPLOYMENT',
  POST_ACCIDENT = 'POST_ACCIDENT',
  RANDOM = 'RANDOM',
}

/**
 * How a drug-screening sample is collected and where. Mirrors the
 * RequirementType taxonomy (DRUG_TEST = URINE, SALIVA_DRUG_TEST = SALIVA,
 * URGENT_CARE_DRUG_TEST = URGENT_CARE).
 */
export enum DrugCollectionMethod {
  URINE = 'URINE',
  SALIVA = 'SALIVA',
  URGENT_CARE = 'URGENT_CARE',
}

export const DEFAULT_DRUG_TEST_EXPIRATION_DATE_DAYS = 3
export const MIN_DRUG_TEST_EXPIRATION_DATE_DAYS = 2
export const MAX_DRUG_TEST_EXPIRATION_DATE_DAYS = 6
export const DRUG_PANEL_DERIVED_TYPES_SUFFIXES = ['POST_ACCIDENT', 'RANDOM']

export type DrugScreeningCombination = {
  panelType: DrugPanelType
  testType: DrugTestType
}

export const isValidDrugScreeningCombination = (
  panel: DrugPanelType,
  testType: DrugTestType,
): boolean => {
  // Rapid panels are only allowed for pre-employment
  if (
    (panel === DrugPanelType.SIX_PANEL_RAPID ||
      panel === DrugPanelType.TEN_PANEL_RAPID) &&
    testType !== DrugTestType.PRE_EMPLOYMENT
  ) {
    return false
  }
  // Labcorp panels (including Express) are PRE_EMPLOYMENT only
  if (
    (panel === DrugPanelType.LABCORP_EXPRESS_6_PANEL ||
      panel === DrugPanelType.LABCORP_EXPRESS_10_PANEL ||
      panel === DrugPanelType.LABCORP_5_PANEL ||
      panel === DrugPanelType.LABCORP_10_PANEL) &&
    testType !== DrugTestType.PRE_EMPLOYMENT
  ) {
    return false
  }
  return true
}

export enum DrugScreeningVerificationStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  DENIED = 'DENIED',
}

export enum DrugScreeningOrderStatus {
  PENDING = 'PENDING',
  SCHEDULED = 'SCHEDULED',
  EXPIRED = 'EXPIRED',
  COMPLETED = 'COMPLETED',
  FILLED = 'FILLED',
  CANCELED = 'CANCELED',
  WAIVER_SIGNED = 'WAIVER_SIGNED',
  SAMPLE_COLLECTED = 'SAMPLE_COLLECTED',
}

export enum DrugScreeningResultStatus {
  POSITIVE = 'POSITIVE',
  NEGATIVE = 'NEGATIVE',
  TEMPORARILY_EXEMPTED = 'TEMPORARILY_EXEMPTED',
  UNAVAILABLE = 'UNAVAILABLE',
  REVIEW_REQUIRED_UNKNOWN_STATUS = 'REVIEW_REQUIRED_UNKNOWN_STATUS',
}

export interface DrugScreeningOrder {
  id: string
  workerId: string
  status: DrugScreeningOrderStatus
  drugScreeningVendor: string
  vendorOrderId: string
  drugPanelType: DrugPanelType
  requestingCompanyId?: string
  expiresAt: Date
  labPassportId: string
  lastReminderSentAt?: Date
  applicantPortalUrl: string
  labPassportEmailedAt?: Date
  waiverSignedAt: Date
  scheduledBy: string
  createdAt: Date
  updatedAt: Date
  requestingCompanyName?: string
  additionalInformation?: string
  sampleCollectionTimestamp?: Date
  employmentType: EmploymentType
  shiftRequestId?: string
}

export interface DrugScreeningResult {
  id: string
  createdAt: Date
  updatedAt: Date
  workerId: string
  drugScreeningResultStatus: DrugScreeningResultStatus
  drugScreeningVendor: string
  vendorOrderId: string
  drugPanelType: DrugPanelType
  requestingCompanyId?: string
  expiresAt: Date
  labPassportId: string
  lastReminderSentAt?: Date
  applicantPortalUrl: string
  labPassportEmailedAt?: Date
  waiverSignedAt: Date
  scheduledBy: string
  verificationStatus: DrugScreeningVerificationStatus
  requestingCompanyName?: string
  resultFileUrl?: string
  shiftRequestId?: string
  employmentType: EmploymentType
  drugScreeningOrder?: DrugScreeningOrder
}

export type CreateDrugScreeningOrderParams = {
  workerId: string
  drugPanelType: DrugPanelType
  shiftRequestId: string
  expirationDays: number
  employmentType: EmploymentType
  vendor: DrugScreeningVendor
}

// V2 company-fact code references this enum as `trigger`; re-export under a
// matching name for naming consistency. Both names refer to the same enum.
export { DrugTestType as DrugTestTrigger }
